import { useCallback, useEffect, useState } from "react";
import _ from "lodash";

import { EventIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { format, FORMATS, isValid } from "utils/datetime";

import InputAdornment from "components/InputAdornment";
import Stack from "components/Stack";
import StaticDatePicker from "components/StaticDatePicker";
import MuiTextField from "components/TextField";

import { ICompanySectionAutomaticFillField } from "entities/Deal/sdk";
import { CompanyFieldFormatType } from "entities/Field/constants";

import { validationSchema } from "./constants";

export interface IAutomaticFillReviewField {
  isDisabled: boolean;
  value: string;
  setReviewedData: ({
    fieldId,
    value,
  }: {
    fieldId: number;
    value: string;
  }) => void;
  field: ICompanySectionAutomaticFillField;
  errors: {
    [key: number]: string | null;
  };
  setErrors: ({
    fieldId,
    value,
  }: {
    fieldId: number;
    value: string | null;
  }) => void;
}

const AutomaticFillReviewField = ({
  isDisabled,
  value,
  setReviewedData,
  field,
  errors,
  setErrors,
}: IAutomaticFillReviewField) => {
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);

  const validateField = useCallback(
    ({
      fieldType,
      value,
    }: {
      fieldType: CompanyFieldFormatType;
      value: string;
    }) => {
      try {
        validationSchema[fieldType].validateSync(value);
        setErrors({ fieldId: field.id, value: null });
      } catch (error) {
        setErrors({ fieldId: field.id, value: (error as Error).message });
      }
    },
    [field.id, setErrors]
  );

  const formatDate = useCallback(({ date }: { date: Date }) => {
    const formattedDate = format(date, FORMATS.CUSTOM_DATE_PICKER.DATE);
    return !_.isNil(formattedDate) ? formattedDate : "";
  }, []);

  const updateDate = useCallback(
    ({ newDate }: { newDate: Date | null }) => {
      setReviewedData({
        fieldId: field.id,
        value: !_.isNil(newDate) ? formatDate({ date: newDate }) : "",
      });
    },
    [field.id, formatDate, setReviewedData]
  );

  useEffect(() => {
    validateField({
      fieldType: field.field_format_type as CompanyFieldFormatType,
      value: value,
    });
  }, [value, field.field_format_type, validateField]);

  const isDateValid = useCallback(({ date }: { date: string }) => {
    if (isValid(new Date(date))) {
      const parsedDate = new Date(date);
      if (date === format(parsedDate, FORMATS.CUSTOM_DATE_PICKER.DATE)) {
        return parsedDate;
      }
    }
    return new Date();
  }, []);

  return (
    <Stack position="relative">
      <MuiTextField
        size="small"
        InputProps={{
          startAdornment: field.field_format_type ===
            CompanyFieldFormatType.CURRENCY_USD && (
            <InputAdornment position="start">$</InputAdornment>
          ),
          endAdornment: field.field_format_type ===
            CompanyFieldFormatType.DATE && (
            <InputAdornment
              position="start"
              onClick={() => setShowDatePicker((prevState) => !prevState)}
              sx={{ cursor: "pointer" }}
            >
              <EventIcon />
            </InputAdornment>
          ),
        }}
        disabled={isDisabled}
        sx={{
          backgroundColor: colors.white,
          opacity: isDisabled ? 0.4 : 1,
          transition: "opacity 0.3s linear",
        }}
        slotProps={{
          formHelperText: {
            style: {
              backgroundColor: colors.gray10,
              margin: 0,
              padding: theme.spacing(0.2, 0),
            },
          },
        }}
        error={Boolean(errors[field.id])}
        helperText={errors[field.id] !== "" && errors[field.id]}
        value={value}
        onChange={(e) => {
          setReviewedData({ fieldId: field.id, value: e.target.value });
        }}
      />
      {showDatePicker && (
        <Stack
          position="absolute"
          top={40}
          right={0}
          zIndex={2}
          borderRadius={8}
        >
          <StaticDatePicker
            slotProps={{
              toolbar: { sx: { display: "none" } },
              layout: {
                sx: {
                  boxShadow: 2,
                  borderRadius: 2,
                },
              },
            }}
            onAccept={(newDate) => {
              updateDate({ newDate });
            }}
            defaultValue={isDateValid({ date: value })}
            onClose={() => setShowDatePicker(false)}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default AutomaticFillReviewField;
